/**
 * Server-rendered preview of the printable map.
 *
 * The image comes from the same renderer that produces the PDF, at a lower DPI,
 * so what is shown here is what gets printed. That is why this replaced the
 * Mapbox preview: Mapbox content cannot lawfully be printed, and a separate
 * client-side preview would inevitably drift from the real output.
 *
 * The first render of a property fetches USDA and USGS imagery and takes several
 * seconds. Later changes to the title or colours recompose from a cached basemap,
 * so they come back quickly. Requests are debounced and superseded to keep the
 * federal services from being hammered while someone drags a slider.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { FiAlertTriangle, FiRefreshCw } from 'react-icons/fi';
import { mapPrintService } from '../services/mapPrintService';
import { YButton } from '../../../ui/YButton';
import {
  DEFAULT_BOUNDARY_LINE_SCALE,
  DEFAULT_ORIENTATION,
} from '../pages/printableMapConstants';

const DEBOUNCE_MS = 700;

export function ServerMapPrintPreview({
  huntBoundaryId,
  size,
  title,
  subtitle = '',
  orientation = DEFAULT_ORIENTATION,
  boundaryLineColor,
  boundaryFillOpacity,
  boundaryLineScale = DEFAULT_BOUNDARY_LINE_SCALE,
  logoUrl = '',
  logoPosition = 'left',
  width = 1000,
  frameHeight = 380,
  onRendered,
}) {
  const [image, setImage] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const abortRef = useRef(null);

  // Held in a ref so an inline callback from the parent cannot retrigger the
  // fetch on every render, which would loop against the federal services.
  const onRenderedRef = useRef(onRendered);
  useEffect(() => {
    onRenderedRef.current = onRendered;
  }, [onRendered]);

  useEffect(() => {
    if (!huntBoundaryId) {
      setImage(null);
      setMeta(null);
      return undefined;
    }

    const timer = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      setError('');
      try {
        const data = await mapPrintService.renderPreview(
          {
            hunt_boundary_id: Number(huntBoundaryId),
            size,
            title: title?.trim() || '',
            subtitle: subtitle?.trim() || '',
            orientation,
            boundary_line_color: boundaryLineColor || '',
            boundary_fill_opacity: boundaryFillOpacity,
            boundary_line_scale: boundaryLineScale,
            ...(logoUrl?.trim()
              ? { logo_url: logoUrl.trim(), logo_position: logoPosition || 'left' }
              : {}),
            width,
          },
          { signal: controller.signal }
        );
        if (controller.signal.aborted) return;
        setImage(data.image);
        setMeta(data);
        if (onRenderedRef.current) onRenderedRef.current(data);
      } catch (err) {
        if (controller.signal.aborted || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
        const status = err?.response?.status;
        setError(
          status === 429
            ? 'Too many previews for now. Wait a moment and try again.'
            : err?.response?.data?.detail
              || 'Could not render the preview. Please try again.'
        );
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [huntBoundaryId, size, title, subtitle, orientation, boundaryLineColor, boundaryFillOpacity, boundaryLineScale, logoUrl, logoPosition, width, attempt]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const fallbackAspect = orientation === 'landscape' ? 4 / 3 : 3 / 4;
  const aspect = meta?.width && meta?.height ? meta.width / meta.height : fallbackAspect;

  return (
    <div className="d-flex flex-column align-items-center gap-2" data-testid="server-map-print-preview">
      <div
        className="position-relative overflow-hidden"
        style={{
          width: Math.round(frameHeight * aspect),
          height: frameHeight,
          maxWidth: '100%',
          borderRadius: 6,
          border: '1px solid rgba(0,0,0,0.25)',
          boxShadow: 'var(--shadow-3, 0 10px 24px rgba(0,0,0,0.12))',
          background: 'var(--color-surface-muted, #e5e7eb)',
        }}
      >
        {image ? (
          <img
            src={image}
            alt={title?.trim() ? `Print preview of ${title.trim()}` : 'Print preview'}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              opacity: loading ? 0.45 : 1,
              transition: 'opacity 150ms ease',
            }}
          />
        ) : null}

        {loading ? (
          <div
            className="position-absolute top-50 start-50 translate-middle text-center px-3"
            style={{ zIndex: 5 }}
          >
            <Spinner animation="border" size="sm" />
            <div className="small mt-2 text-body-secondary">
              {image ? 'Updating…' : 'Rendering from aerial imagery…'}
            </div>
            {!image ? (
              <div className="small text-body-secondary">This takes a few seconds the first time.</div>
            ) : null}
          </div>
        ) : null}

        {!image && !loading && error ? (
          <div className="position-absolute top-50 start-50 translate-middle text-center px-3" style={{ zIndex: 5 }}>
            <FiAlertTriangle aria-hidden="true" className="text-warning mb-2" size={22} />
            <div className="small text-body-secondary mb-2">{error}</div>
            <YButton type="button" variant="secondary" onClick={() => setAttempt((n) => n + 1)}>
              <FiRefreshCw aria-hidden="true" />
              Retry
            </YButton>
          </div>
        ) : null}

        {!image && !loading && !error && !huntBoundaryId ? (
          <div className="position-absolute top-50 start-50 translate-middle text-center px-3 small text-body-secondary">
            Select a boundary to see a preview.
          </div>
        ) : null}
      </div>

      {image && error ? <div className="small text-warning mb-0">{error}</div> : null}

      {meta ? (
        <div className="text-center">
          <div className="pm-mono-label">
            {`${Number(meta.acres).toLocaleString()} ACRES · ${meta.effective_dpi} DPI`}
          </div>
          {!meta.is_native_resolution ? (
            <div className="small text-warning mt-1">
              This property is small for a {size.replace('x', ' × ')}″ print, so imagery is being
              enlarged. A smaller size will look sharper.
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
